import type { AuthResponse, SessionUser } from '@forge/shared';

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details?: unknown;

  constructor(status: number, message: string, code = 'error', details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }

  /** Flattens server-side zod issues into readable lines. */
  get issues(): string[] {
    const details = this.details as { issues?: { path: string; message: string }[]; problems?: string[] } | undefined;
    if (details?.problems) return details.problems;
    if (details?.issues) return details.issues.map((i) => (i.path ? `${i.path}: ${i.message}` : i.message));
    return [];
  }
}

/**
 * The access token lives in memory only. Persistence comes from the httpOnly
 * refresh cookie, so a stolen XSS payload cannot read a long-lived credential
 * out of localStorage.
 */
let accessToken: string | null = null;
let currentUser: SessionUser | null = null;
let refreshPromise: Promise<boolean> | null = null;

const listeners = new Set<(user: SessionUser | null) => void>();

export function onAuthChange(listener: (user: SessionUser | null) => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function setSession(session: AuthResponse | null): void {
  accessToken = session?.accessToken ?? null;
  currentUser = session?.user ?? null;
  for (const listener of listeners) listener(currentUser);
}

export function getAccessToken(): string | null {
  return accessToken;
}

export function getCurrentUser(): SessionUser | null {
  return currentUser;
}

const BASE = '/api';

interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  /** Skip the automatic refresh-and-retry (used by the refresh call itself). */
  raw?: boolean;
  socketId?: string;
}

async function parseError(res: Response): Promise<ApiError> {
  let payload: { error?: string; code?: string; details?: unknown } = {};
  try {
    payload = (await res.json()) as typeof payload;
  } catch {
    /* non-JSON error body */
  }
  return new ApiError(res.status, payload.error ?? res.statusText ?? 'Request failed', payload.code, payload.details);
}

/** Refreshes the access token, collapsing concurrent callers onto one request. */
export async function refreshSession(): Promise<boolean> {
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    try {
      const res = await fetch(`${BASE}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
      });
      if (!res.ok) {
        setSession(null);
        return false;
      }
      setSession((await res.json()) as AuthResponse);
      return true;
    } catch {
      setSession(null);
      return false;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { body, raw, socketId, headers, ...rest } = options;

  const send = async (): Promise<Response> =>
    fetch(`${BASE}${path}`, {
      ...rest,
      credentials: 'include',
      headers: {
        ...(body !== undefined ? { 'content-type': 'application/json' } : {}),
        ...(accessToken ? { authorization: `Bearer ${accessToken}` } : {}),
        ...(socketId ? { 'x-forge-socket': socketId } : {}),
        ...(headers as Record<string, string> | undefined),
      },
      body: body === undefined ? undefined : JSON.stringify(body),
    });

  let res = await send();

  // One transparent retry after refreshing an expired access token.
  if (res.status === 401 && !raw && accessToken !== null) {
    if (await refreshSession()) res = await send();
  }

  if (!res.ok) throw await parseError(res);
  if (res.status === 204) return undefined as T;

  const contentType = res.headers.get('content-type') ?? '';
  if (!contentType.includes('json')) return (await res.text()) as unknown as T;
  return (await res.json()) as T;
}

export const api = {
  get: <T>(path: string, options?: RequestOptions) => request<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'POST', body }),
  patch: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'PATCH', body }),
  put: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'PUT', body }),
  delete: <T>(path: string, options?: RequestOptions) => request<T>(path, { ...options, method: 'DELETE' }),
};

/* --------------------------------- auth ---------------------------------- */

export async function login(email: string, password: string): Promise<SessionUser> {
  const session = await request<AuthResponse>('/auth/login', {
    method: 'POST',
    body: { email, password },
    raw: true,
  });
  setSession(session);
  return session.user;
}

export async function register(name: string, email: string, password: string): Promise<SessionUser> {
  const session = await request<AuthResponse>('/auth/register', {
    method: 'POST',
    body: { name, email, password },
    raw: true,
  });
  setSession(session);
  return session.user;
}

export async function logout(): Promise<void> {
  try {
    await request('/auth/logout', { method: 'POST', raw: true });
  } finally {
    setSession(null);
  }
}

export async function changePassword(currentPassword: string, newPassword: string): Promise<void> {
  const session = await request<AuthResponse>('/auth/change-password', {
    method: 'POST',
    body: { currentPassword, newPassword },
  });
  setSession(session);
}

/** Called once on boot: revives a session from the refresh cookie. */
export async function restoreSession(): Promise<SessionUser | null> {
  const ok = await refreshSession();
  return ok ? currentUser : null;
}

/** Re-reads the user (memberships change when someone is added to a project). */
export async function reloadUser(): Promise<SessionUser | null> {
  try {
    const { user } = await api.get<{ user: SessionUser }>('/auth/me');
    currentUser = user;
    for (const listener of listeners) listener(currentUser);
    return user;
  } catch {
    return currentUser;
  }
}
